import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHoverNavigationContext } from './hover-navigation-context';
import { useNavigationMode } from './navigation-mode-context';
import NavigationSettings from './navigation-settings';
import { hoverHighlightSx } from './hover-style';

function useStandardActions() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { active: hoverActive, toggle: toggleHover } = useHoverNavigationContext();
    const { navigationMode, xyCursorActive, toggleXyCursor } = useNavigationMode();

    const toggleNavigation = useCallback(() => {
        if (navigationMode === 'hover') {
            toggleHover();
        } else if (navigationMode === 'xy_cursor') {
            toggleXyCursor();
        }
    }, [navigationMode, toggleHover, toggleXyCursor]);

    const isNavigationActive = navigationMode === 'hover' ? hoverActive : xyCursorActive;

    const leftActions = useMemo(() => [
        {
            icon: HomeIcon,
            onClick: () => navigate('/main/applications'),
        },
    ], [navigate]);

    const rightActions = useMemo(() => [
        {
            icon: isNavigationActive ? GpsOffIcon : GpsFixedIcon,
            onClick: toggleNavigation,
        },
    ], [toggleNavigation, isNavigationActive]);

    return { leftActions, rightActions };
}

const actionBarSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0 0 16px 0',
    gap: 2,
};

const actionGroupSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
};

const actionButtonSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
    borderRadius: 4,
    gap: 1,
    bgcolor: 'grey.300',
    color: 'text.primary',
    px: 3,
    py: 2,
    minWidth: 140,
};

/**
 * Apps Action Bar with integrated Settings modal - Split layout
 *
 * Left side: User-accessible actions (included in hover navigation)
 * - Custom actions (e.g., "Back to Level")
 * - Home
 * - Navigation Toggle
 *
 * Right side: Helper/caregiver actions (NOT in hover navigation, direct click only)
 * - Helper actions (e.g., Grid's Edit button)
 * - Settings
 *
 * @param {Object} props
 * @param {Array} props.customActions - Custom actions for left side (included in hover navigation)
 * @param {Array} props.helperActions - Helper actions for right side (NOT in hover navigation)
 * @param {number} props.activeIndex - Current active index from hover navigation (relative to left-side actions only)
 * @param {boolean} props.active - Whether hover navigation is active
 * @param {function} props.onActionsChange - Callback that receives the left-side actions array (for parent's onSelect handling)
 * @param {string} props.flex - Flex CSS value for the bar (default: '0 0 auto')
 */
export default function AppsActionBarWithSettings({
    customActions = [],
    helperActions = [],
    activeIndex = -1,
    active = false,
    onActionsChange = null,
    flex = '0 0 auto'
}) {
    const { t } = useTranslation();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const { leftActions: standardLeftActions, rightActions: standardRightActions } = useStandardActions();

    // Left side: standard left + custom actions (accessible via hover navigation)
    const leftActions = [...standardLeftActions, ...customActions];

    // Right side: helper actions + navigation toggle + settings (NOT accessible via hover navigation)
    const settingsAction = {
        icon: SettingsIcon,
        onClick: () => setSettingsOpen(true),
    };
    const rightActions = [...helperActions, ...standardRightActions, settingsAction];

    // Notify parent of LEFT-side actions only (for hover navigation)
    useEffect(() => {
        if (onActionsChange) {
            onActionsChange(leftActions);
        }
    }, [leftActions, onActionsChange]);

    const handleSettingsClose = () => {
        setSettingsOpen(false);
    };

    const handleSaveSuccess = () => {
        // Optionally close modal after successful save
        // setTimeout(() => setSettingsOpen(false), 1500);
    };

    const renderActionButton = (action, index, isLeftSide) => {
        const Icon = action.icon;
        const isHovered = isLeftSide && active && activeIndex === index;

        return (
            <Box
                key={index}
                onClick={action.onClick}
                sx={{
                    ...actionButtonSx,
                    ...(isHovered && hoverHighlightSx),
                }}
            >
                <Icon sx={{ fontSize: 32 }} />
                {action.label && (
                    <Box component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                        {action.label}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            <Box sx={{ ...actionBarSx, flex }}>
                {/* Left side: User-accessible actions (hover navigation enabled) */}
                <Box sx={actionGroupSx}>
                    {leftActions.map((action, index) => renderActionButton(action, index, true))}
                </Box>

                {/* Right side: Helper actions (no hover navigation) */}
                <Box sx={actionGroupSx}>
                    {rightActions.map((action, index) => renderActionButton(action, index, false))}
                </Box>
            </Box>

            {/* Settings Modal */}
            <Dialog
                open={settingsOpen}
                onClose={handleSettingsClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('PROFILE.SETTINGS_TITLE')}
                    <IconButton
                        edge="end"
                        onClick={handleSettingsClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <NavigationSettings onSaveSuccess={handleSaveSuccess} />
                </DialogContent>
            </Dialog>
        </>
    );
}
